<!-- 
Risk profile chart for a provider.
-->
<template>
  <v-flex xs12 lg6>
    <v-card flat>
      <v-card-title>
        <span class="title">{{ title }}</span>
      </v-card-title>
      <our-loading-indicator v-if="loading"/>
      <v-card-text class="pb-3">
        <line-chart 
          v-if="hasData" 
          ref="chart" 
          :chart-data="chartData" 
          :chart-options="chartOptions" 
          style="height: 265px"
        />
        <p v-if="!loading && !hasData">{{ noDataMessage }}</p>
      </v-card-text>
    </v-card>
    <table v-if="hasData && chartData.datasets.length > 0" class="ma-3" style="width: 100%;">
      <thead>
        <th class="text-xs-left">Risk factor</th>
        <th v-for="label in chartData.labels" :key="label" class="text-xs-left">{{ label }}</th>
      </thead>
      <tbody>
        <tr v-for="data in chartData.datasets" :key="data.label">
          <td>{{ data.label }}</td>
          <td v-for="(value, i) in data.data" :key="i">{{ value }}</td>
        </tr>
      </tbody>
    </table>
  </v-flex>
</template>

<script>
import _ from "underscore";
import LineChart from "@/components/common/chart/LineChart.vue";
import lookupMixins from "@/mixins/lookup.mixins";
import * as action from "@/store/providers/action-types";
import * as chartHelper from "@/components/common/chart/helper.js";
import * as formattingHelper from "@/helpers/formatting.helper.js";

export default {
  components: { LineChart },
  mixins: [lookupMixins],
  props: {
    providerId: {
      type: Number,
      required: true
    },
    levyCollectionType: {
      type: String,
      required: true
    }
  },
  computed: {
    isVsl() {
      return this.levyCollectionType === "Vsl";
    },
    isHelp() {
      return this.levyCollectionType === "Help";
    },
    isUpfront() {
      return this.levyCollectionType === "Upfront";
    },
    chartOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          yAxes: [{ ticks: { min: 0 }, afterBuildTicks: this.setYAxesTicks }]
        }
      };
    },
    state() {
      return this.isVsl
        ? this.$store.state.providers.riskProfileVsl
        : this.isHelp
          ? this.$store.state.providers.riskProfileHelp
          : this.isUpfront
            ? this.$store.state.providers.riskProfileUpfront
            : null;
    },
    loading() {
      return this.state.loading;
    },
    loaded() {
      return this.state.loaded;
    },
    hasData() {
      return this.state.data.length > 0;
    },
    noDataMessage() {
      return `This provider does not have a ${this.title} as they have not been added to a ${
        this.levyCollectionDescription
      } levy collection yet.`;
    },
    chartData() {
      if (!this.loaded) return { labels: [""], datasets: [] };
      const labels = _(this.state.data).pluck("year");
      var keys = _.keys(this.state.data[0]);
      keys = _.without(keys, "year");
      keys = _.map(keys, score => score[0].toUpperCase() + score.substr(1));
      var datasets = [];
      keys.forEach((key, i) => {
        var dataset = {
          label: formattingHelper.splitIntoWords(key),
          fill: "origin",
          borderColor: chartHelper.DefaultColors[i],
          backgroundColor: chartHelper.DefaultColors[i] + `${(i + 1) * 10}`
        };
        dataset.data = _.pluck(this.state.data, key[0].toLowerCase() + key.substr(1));
        datasets.push(dataset);
      });
      return { labels, datasets };
    },
    title() {
      return `${this.levyCollectionDescription} risk profile`;
    },
    levyCollectionDescription() {
      return this.isVsl ? "VSL" : this.isHelp ? "HELP" : this.isUpfront ? "Up-front" : "UNKNOWN";
    }
  },
  watch: {
    providerId() {
      this.refresh();
    },
    loaded() {
      if (this.loaded) {
        this.drawChart();
      }
    }
  },
  mounted() {
    this.refresh();
  },
  methods: {
    refresh() {
      this.$store.dispatch(
        this.isVsl
          ? action.RISK_PROFILE_VSL
          : this.isHelp
            ? action.RISK_PROFILE_HELP
            : this.isUpfront
              ? action.RISK_PROFILE_UPFRONT
              : null,
        this.providerId
      );
    },
    drawChart() {
      this.$nextTick(() => {
        if (this.$refs.chart) this.$refs.chart.display();
      });
    }
  }
};
</script>
